// src/components/desk/contract/StatusChip.jsx — ONE status chip for a contract, used by the
// meeting card's block (ContractPanel) and the page (ContractPage) alike (designer sheet 4a):
//   DRAFT · SENT · 1 OF 2 SIGNED · CLOSED · DECLINED · VOID
// The status is the server's DERIVED one (API §ContractDetail); the "N of M signed" count is
// read off the parties — required ones with a signature on the CURRENT version — so the chip
// never disagrees with the signature rows beside it.

import { t, tLog, tOr } from '../../../lib/i18n.js'

export function signedCount(c) {
  const parties = (c?.parties || []).filter(p => p.required !== false)
  const v = c?.current_version?.version
  const n = parties.filter(p => p.signature && (p.signature.version == null || v == null || p.signature.version === v)).length
  return { n, total: parties.length }
}

export function statusChipLabel(c) {
  const s = c?.status
  const remote = c?.remote_rental?.state
  if (s === 'signed' && remote) return ({
    signed_pending_initial_payment: t('desk_contract_chip_remote_pending_payment'),
    // effective_if_paid replaced payment_condition_failed (owner 2026-09-21): no recorded payment
    // is not proof of no payment. The old key stays mapped for a server not yet redeployed.
    effective_if_paid: t('desk_contract_chip_remote_if_paid'),
    payment_condition_failed: t('desk_contract_chip_remote_if_paid'), effective_scheduled: t('desk_contract_chip_remote_scheduled'),
    effective_awaiting_handover: t('desk_contract_chip_remote_awaiting_handover'), effective_handed_over: t('desk_contract_chip_remote_handed_over'),
  })[remote] || remote
  // 🔁 a signed edition of a tenancy that a later one replaced, or one signed but not yet in force (core 3097)
  if (s === 'signed' && c?.revision?.chain_state === 'superseded') return t('desk_contract_chip_superseded')
  if (s === 'signed' && c?.revision?.chain_state === 'not_yet_effective') return t('desk_contract_chip_not_yet_effective')
  if (s === 'signed') return t('desk_contract_chip_closed')
  if (s === 'partially_signed') {
    const { n, total } = signedCount(c)
    return tLog('desk_contract_chip_signed_of', { n, total })
  }
  return tOr(`desk_contract_status_${s}`, s || '')
}

// Tones. The chip always sits on a DARK bar (the page header, the meeting-card block, the
// right column), so the tints are paper-on-ink. Only CLOSED carries the accent, so the one
// colour keeps meaning "done".
const TONE = {
  draft: 'border border-[#F7F5F0]/30 text-[#F7F5F0]/75',
  sent: 'bg-[#F7F5F0]/10 border border-[#F7F5F0]/40 text-[#F7F5F0]',
  partially_signed: 'bg-amber-400/20 text-amber-200 border border-amber-400/50',
  signed: 'bg-[#1F6F4F] text-[#F7F5F0] border border-[#1F6F4F]',
  signed_pending_initial_payment: 'bg-amber-400/20 text-amber-200 border border-amber-400/50',
  effective_if_paid: 'bg-amber-400/20 text-amber-200 border border-amber-400/50',
  payment_condition_failed: 'bg-amber-400/20 text-amber-200 border border-amber-400/50',
  effective_scheduled: 'bg-amber-400/20 text-amber-200 border border-amber-400/50',
  effective_awaiting_handover: 'bg-[#1F6F4F] text-[#F7F5F0] border border-[#1F6F4F]',
  effective_handed_over: 'bg-[#1F6F4F] text-[#F7F5F0] border border-[#1F6F4F]',
  declined: 'bg-red-500/15 text-red-300 border border-red-400/50',
  void: 'border border-dashed border-[#F7F5F0]/30 text-[#F7F5F0]/45 line-through',
  expired: 'border border-dashed border-[#F7F5F0]/30 text-[#F7F5F0]/45',
  superseded: 'border border-[#F7F5F0]/30 text-[#F7F5F0]/60',
  not_yet_effective: 'bg-amber-400/20 text-amber-200 border border-amber-400/50',
}

export function StatusChip({ contract, className = '' }) {
  const chain = contract?.status === 'signed' && ['superseded', 'not_yet_effective'].includes(contract?.revision?.chain_state) ? contract.revision.chain_state : null
  const s = chain || (contract?.status === 'signed' && contract?.remote_rental?.state) || contract?.status || 'draft'
  return (
    <span data-testid="contract-status-chip" data-status={s}
      className={`inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[.12em] uppercase leading-4 whitespace-nowrap ${TONE[s] || TONE.draft} ${className}`}>
      {statusChipLabel(contract)}
    </span>
  )
}
